import { useCallback, useEffect, useMemo } from "react"

import { DateTime, Interval } from "luxon"

import { isValidDateTimeRange } from "../date"
import { useSaveYear } from "./use-save-year"
import { useSearchParamsYear } from "./use-search-params-year"

interface UseYearProps {
  /** The valid bounds of the year */
  bounds: Interval | undefined

  /** A unique id to identify the year for purposes of saving */
  id: string
}

interface UseYearValue {
  /** The year's date range */
  dateRange: Interval

  /** Sets the year in both the url and its persistence layer */
  setYear: (value: number) => void
}

/**
 * Manages the year value that powers a UI including search params,
 * saving the year, validity, and default value.
 *
 * @example
 * const {
 *   dateRange,
 *   setYear
 * } = useYear({
 *   id: "sitesPerformanceYear",
 *   bounds: Interval.fromDateTimes(
 *     DateTime.fromISO("2024-01-01").startOf("year"),
 *     DateTime.fromISO("2024-12-31").endOf("year"),
 *   )
 * })
 */
export const useYear = ({ id, bounds }: UseYearProps): UseYearValue => {
  const { isYearInSearchParams, searchParamsYear, setSearchParamsYear } =
    useSearchParamsYear()
  const { saveYear, savedYear } = useSaveYear({ id })

  const year: number | null | undefined = Number.isFinite(searchParamsYear)
    ? searchParamsYear
    : savedYear

  const dateRange: Interval = useMemo(
    () =>
      Interval.fromDateTimes(
        DateTime.fromISO(year?.toString() ?? "").startOf("year"),
        DateTime.fromISO(year?.toString() ?? "").endOf("year")
      ),
    [year]
  )

  const setYear = useCallback(
    (value: number): void => {
      setSearchParamsYear(value)
      saveYear(value)
    },
    [saveYear, setSearchParamsYear]
  )

  // Handle missing search param and invalid year
  useEffect(() => {
    if (bounds?.isValid) {
      let newYear: number = dateRange.start?.year ?? NaN

      // Use default year if range is invalid
      if (!isValidDateTimeRange(dateRange, bounds)) {
        newYear = Math.min(
          DateTime.now().minus({ years: 1 }).year,
          bounds.end?.year ?? Infinity
        )
      }

      // Ensure correct year is in url
      if (newYear !== dateRange.start?.year || !isYearInSearchParams) {
        setSearchParamsYear(newYear, { replace: true })
      }

      // Ensure year is saved if necessary
      if (newYear !== savedYear) {
        saveYear(newYear)
      }
    }
  }, [
    dateRange,
    isYearInSearchParams,
    saveYear,
    savedYear,
    setSearchParamsYear,
    bounds,
  ])

  return { dateRange, setYear }
}
